/**
 * LocationInputComponent for React Native
 * Public API that delegates to platform-specific implementations
 */

import type {
  LocationAutocompleteUserOptions,
  LocationInputComponentResult,
  LocationInputError,
  LocationInputLogData,
  CustomTagConfig,
  PrefillLocation,
} from "./LocationInputComponentCore";
import type { ViewStyle } from "react-native";

import { LocationInputElement } from "./LocationInputElement";

import React, { type ReactNode } from "react";

// Export only the types needed by consumers
// Note: We do NOT export LocationInputComponentCore itself - it's internal
export type {
  LocationSuggestion,
  Gen2Location,
} from "./util/fetchLocationAutocompleteSuggestionsViaGraphql";

export { LocationInputErrorCode } from "./LocationInputComponentCore";
export type {
  LocationAutocompleteUserOptions,
  LocationInputComponentResult,
  LocationInputError,
  LocationInputLogData,
  CustomTagConfig,
  PrefillLocation,
} from "./LocationInputComponentCore";

export interface LocationInputComponentProps {
  // Required Props
  /** Configuration for the autocomplete service */
  options: LocationAutocompleteUserOptions;

  /** Locale used for error message translations (e.g., 'en_US') */
  locale: string;

  /** Label for the form field. Omit to render the input without a built-in label. */
  label?: ReactNode;

  /** Callback fired when a location is selected or input loses focus */
  onValueSelected: (item: LocationInputComponentResult, logData?: LocationInputLogData) => void;

  // Optional Props
  /** Pre-populate the input with a location */
  prefill?: string | PrefillLocation;

  /** Indicates if the string prefill is a precise location */
  preciseLocation?: boolean;

  /** If true, treats empty input as invalid */
  required?: boolean;

  /** Callback fired when an error occurs */
  onLocationError?: (error?: LocationInputError, logData?: LocationInputLogData) => void;

  /** Accessibility label for the input field */
  ariaLabel?: string;

  /** Helper text displayed below the input field */
  helperText?: string;

  /** Session ID for tracking multiple components on the same page */
  pageSessionId?: string;

  /** Name of the client page containing the component */
  clientPage?: string;

  /** Props to pass to suggestion items (currently supports data-dd prefixed attributes) */
  suggestionItemInnerProps?: (index: number) => Record<string, any>;

  /** Configuration for custom tags displayed alongside locations */
  customTags?: CustomTagConfig[];

  /** Test ID for testing */
  testID?: string;

  /** Input field style */
  inputStyle?: ViewStyle;

  /**
   * Style overrides for the autocomplete suggestions container. Useful for
   * absolute-positioning the dropdown so it overlays surrounding layout
   * (e.g. when LIC is inside a fixed-size modal and the dropdown would
   * otherwise push content below it).
   */
  optionsWrapperStyle?: ViewStyle;

  /** Whether structured location validation is enabled (e.g. via A/B test) */
  useStructuredValidation?: boolean;

  /** Placeholder text shown in the input field. */
  placeholder?: string;

  /**
   * Render the autocomplete inline (false) instead of inside a bottom sheet (true).
   * Defaults to true on native to match the bottom-sheet UX.
   */
  useModal?: boolean;
}

/**
 * LocationInputComponent - Public API
 * Delegates to platform-specific implementations via LocationInputElement
 */
export const LocationInputComponent: React.FC<LocationInputComponentProps> = (props) => {
  return <LocationInputElement {...props} />;
};

// Export as default
export default LocationInputComponent;

// Export BottomSheetModalProvider for consumers to wrap their app/section
export { BottomSheetModalProvider } from "./components/BottomSheetModalProvider";
